import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
	ABSCHNITT_REITER, AbschnittId, AbschnittReiter, abschnitteFuer, hatAbschnittInhalt, kurzfassung,
} from './punkt-abschnitte';

export interface WahlZeile {
	id: AbschnittId;
	titel: string;
	/** Die Quelle hat in diesem Abschnitt Angaben - nur dann ist er wählbar. */
	vorhanden: boolean;
	kurz: string;
	/** Der Zielpunkt hat hier schon Angaben; eine Übernahme ersetzt sie. */
	belegt: boolean;
	zielKurz: string;
}

export interface WahlGruppe {
	reiter: AbschnittReiter;
	zeilen: WahlZeile[];
}

/**
 * Die Zeilen der Hakenliste, gruppiert nach Reiter des Protokolls. Ohne `ziel` (neuer
 * Punkt) ist nichts belegt.
 */
export function wahlGruppen(quelle: unknown, ziel: unknown, punktart: string | undefined): WahlGruppe[] {
	const abschnitte = abschnitteFuer(punktart);
	return ABSCHNITT_REITER
		.map(reiter => ({
			reiter,
			zeilen: abschnitte.filter(a => a.reiter === reiter).map(a => ({
				id: a.id,
				titel: a.titel,
				vorhanden: !!quelle && hatAbschnittInhalt(quelle, a.id),
				kurz: quelle ? kurzfassung(quelle, a.id) : '',
				belegt: !!ziel && hatAbschnittInhalt(ziel, a.id),
				zielKurz: ziel ? kurzfassung(ziel, a.id) : '',
			})),
		}))
		.filter(g => g.zeilen.length > 0);
}

/**
 * Haken je Abschnitt, gruppiert nach Reiter, mit einem Haken für den ganzen Reiter.
 * Benutzt vom Anlege-Fenster (Weg 1) und von „Aus anderem Punkt übernehmen" (Weg 2).
 *
 * Ein Abschnitt ohne Angaben in der Quelle ist nicht wählbar. Hat der Zielpunkt dort
 * schon Angaben, steht „wird ersetzt" daneben (Nutzer-Entscheidung 28.09.2026: erst die
 * Vorschau, dann ersetzen).
 */
@Component({
	selector: 'bp-abschnitt-wahl',
	standalone: true,
	imports: [CommonModule],
	template: `
		@for (g of gruppen; track g.reiter) {
			<div class="wahl-gruppe">
				<label class="wahl-kopf">
					<input type="checkbox" class="form-check-input"
						[checked]="gruppenStand(g) === 'alle'"
						[indeterminate]="gruppenStand(g) === 'teils'"
						[disabled]="!hatWaehlbare(g)"
						(change)="schalteGruppe(g, $any($event.target).checked)">
					<span>{{ g.reiter }}</span>
					<span class="wahl-hinweis">ganzer Reiter</span>
				</label>
				@for (z of g.zeilen; track z.id) {
					<label class="wahl-zeile" [class.wahl-leer]="!z.vorhanden">
						<input type="checkbox" class="form-check-input"
							[checked]="istGewaehlt(z.id)"
							[disabled]="!z.vorhanden"
							(change)="schalte(z.id, $any($event.target).checked)">
						<span class="wahl-text">
							<span class="wahl-titel">
								<span>{{ z.titel }}</span>
								@if (z.belegt && z.vorhanden) {
									@if (istGewaehlt(z.id)) {
										<span class="badge text-bg-warning">wird ersetzt</span>
									} @else {
										<span class="badge text-bg-light border">hier schon ausgefüllt</span>
									}
								}
							</span>
							<span class="wahl-kurz">{{ z.vorhanden ? z.kurz : 'keine Angaben' }}</span>
							@if (z.belegt && z.vorhanden && istGewaehlt(z.id)) {
								<span class="wahl-bisher">
									<span>bisher:</span>
									<span>{{ z.zielKurz }}</span>
								</span>
							}
						</span>
					</label>
				}
			</div>
		}
	`,
	styles: [`
		:host { display: flex; flex-direction: column; gap: 0.75rem; }
		.wahl-gruppe { display: flex; flex-direction: column; gap: 0.25rem; }
		.wahl-kopf, .wahl-zeile { display: flex; align-items: flex-start; gap: 0.5rem; margin: 0; cursor: pointer; }
		.wahl-kopf { font-weight: 600; border-bottom: 1px solid var(--bs-border-color); padding-bottom: 0.25rem; }
		.wahl-kopf .wahl-hinweis { font-weight: normal; font-size: 0.8rem; color: var(--bs-secondary-color); align-self: center; }
		.wahl-zeile { padding: 0.35rem 0 0.35rem 1.25rem; }
		.wahl-zeile .form-check-input, .wahl-kopf .form-check-input { flex: none; margin-top: 0.2rem; width: 1.25rem; height: 1.25rem; }
		.wahl-text { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
		.wahl-titel { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 0.4rem; }
		.wahl-kurz, .wahl-bisher { font-size: 0.8rem; color: var(--bs-secondary-color); overflow-wrap: anywhere; }
		.wahl-bisher { display: inline-flex; flex-wrap: wrap; gap: 0.25rem; }
		.wahl-leer { cursor: default; opacity: 0.6; }
	`],
})
export class AbschnittWahlComponent implements OnChanges {
	/** Der Punkt, aus dem übernommen wird. */
	@Input() quelle: unknown;
	/** Der Punkt, in den übernommen wird; fehlt bei einem neuen Punkt. */
	@Input() ziel: unknown;
	/** Punktart des Zielpunktes: Sie entscheidet, welche Abschnitte es gibt. */
	@Input() punktart = '';
	@Input() gewaehlt: AbschnittId[] = [];
	@Output() gewaehltChange = new EventEmitter<AbschnittId[]>();

	gruppen: WahlGruppe[] = [];

	ngOnChanges(): void {
		this.gruppen = wahlGruppen(this.quelle, this.ziel, this.punktart);
	}

	istGewaehlt(id: AbschnittId): boolean {
		return this.gewaehlt.includes(id);
	}

	schalte(id: AbschnittId, an: boolean): void {
		const ohne = this.gewaehlt.filter(x => x !== id);
		this.gewaehlt = an ? [...ohne, id] : ohne;
		this.gewaehltChange.emit(this.gewaehlt);
	}

	hatWaehlbare(g: WahlGruppe): boolean {
		return g.zeilen.some(z => z.vorhanden);
	}

	gruppenStand(g: WahlGruppe): 'alle' | 'teils' | 'keine' {
		const waehlbar = g.zeilen.filter(z => z.vorhanden);
		const an = waehlbar.filter(z => this.istGewaehlt(z.id)).length;
		if (an === 0) return 'keine';
		return an === waehlbar.length ? 'alle' : 'teils';
	}

	schalteGruppe(g: WahlGruppe, an: boolean): void {
		const ids = new Set(g.zeilen.filter(z => z.vorhanden).map(z => z.id));
		const ohne = this.gewaehlt.filter(x => !ids.has(x));
		this.gewaehlt = an ? [...ohne, ...ids] : ohne;
		this.gewaehltChange.emit(this.gewaehlt);
	}
}
